import { cloneDeep, groupBy, sortBy } from 'lodash-es';
import { type Reducer, useEffect, useMemo, useReducer } from 'react';
import useCollections, { type Collection } from '@/hooks/collections/use-collections';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import { generateSessionKey } from '@/utils/session';

const isMediaTypes = [ConversationMessageContentType.Image, ConversationMessageContentType.Video] as Readonly<
  ConversationMessageContentType[]
>;

const canDownloadTypes = [
  ConversationMessageContentType.Image,
  ConversationMessageContentType.Video,
  ConversationMessageContentType.Audio,
] as Readonly<ConversationMessageContentType[]>;

export default function useFilteredCollections(
  collectionId?: Nullable<number>,
  direction: 'forward' | 'backward' = 'forward',
  filterType: CollectionFilterReducerFilterType = 'all',
) {
  const { collections, isLoading, error } = useCollections();
  const [state, dispatch] = useReducer<CollectionFilterReducer>(
    collectionFilterReducer,
    createInitialState(collections, filterType, direction),
  );

  const [, collectionGroup, indexInGroup, collection] = useMemo(() => {
    let indexInGroup = -1;
    let groupKey = '';
    let collections: Collection[] = [];
    let collection: Collection | undefined;

    if (!collectionId) return [groupKey, collections, indexInGroup, collection];

    for (const key in state.groupedCollections) {
      const group = state.groupedCollections[key];
      if (!group) continue;

      const index = group.findIndex((c) => c.ID === collectionId);
      if (index > -1) {
        indexInGroup = index;
        groupKey = key;
        collections = group;
        collection = group[index];
        if (collection && isMediaTypes.includes(collection.Type)) {
          collections = filterByTypes(group, 'media');
          indexInGroup = collections.findIndex((c) => c.ID === collectionId);
        }
        break;
      }
    }

    return [groupKey, collections, indexInGroup, collection];
  }, [collectionId, state.groupedCollections]);
  const shouldDarkMode = collection ? isMediaTypes.includes(collection.Type) : false;
  const canDownload = collection ? canDownloadTypes.includes(collection.Type) : false;

  useEffect(() => {
    if (collections) {
      dispatch({
        type: 'update-collections',
        params: { collections },
      });
    }
  }, [collections]);

  return {
    groupedCollections: state.groupedCollections,
    shouldDarkMode,
    canDownload,
    collection,
    collectionIndexInGroup: indexInGroup,
    collectionGroup,
    filterType: state.filteredBy,
    setFilterType: (filter: CollectionFilterReducerFilterType) =>
      dispatch({ type: 'update-filter', params: { filter } }),
    isLoading,
    error,
  };
}

export type CollectionFilterReducerFilterType = 'all' | 'text' | 'files' | 'audio' | 'media';

type CollectionFilterReducerAction =
  | {
      type: 'update-filter';
      params: {
        filter: CollectionFilterReducerFilterType;
        collections?: Collection[];
      };
    }
  | { type: 'update-collections'; params: { collections: Collection[] } }
  | {
      type: 'update-direction';
      params: {
        direction: 'forward' | 'backward';
        collections?: Collection[];
      };
    }
  | {
      type: 'reset';
      params: Partial<{
        collections: Collection[];
        direction: 'forward' | 'backward';
        filter: CollectionFilterReducerFilterType;
      }>;
    };

type CollectionFilterReducerState = {
  filteredBy: CollectionFilterReducerFilterType;
  direction: 'forward' | 'backward';
  collections: Collection[];
  groupedCollections: Record<string, Collection[]>;
};

type CollectionFilterReducer = Reducer<CollectionFilterReducerState, CollectionFilterReducerAction>;

function createInitialState(
  collections: Collection[],
  filterType: CollectionFilterReducerFilterType,
  direction: 'forward' | 'backward',
): CollectionFilterReducerState {
  return collectionFilterReducer(
    {
      filteredBy: filterType,
      direction,
      collections,
      groupedCollections: {},
    },
    {
      type: 'reset',
      params: { collections },
    },
  );
}

function collectionFilterReducer(
  state: CollectionFilterReducerState,
  action: CollectionFilterReducerAction,
): CollectionFilterReducerState {
  const newState = cloneDeep(state);

  switch (action.type) {
    case 'update-collections':
      newState.collections = action.params.collections;
      break;
    case 'update-filter':
      newState.filteredBy = action.params.filter;
      newState.collections = action.params.collections ?? state.collections;
      break;
    case 'update-direction':
      newState.direction = action.params.direction;
      newState.collections = action.params.collections ?? state.collections;
      break;
    case 'reset':
      newState.filteredBy = action.params.filter ?? state.filteredBy;
      newState.direction = action.params.direction ?? state.direction;
      newState.collections = action.params.collections ?? state.collections;
      break;
    default:
      return state;
  }

  newState.groupedCollections = groupCollections(
    filterByTypes(newState.collections, newState.filteredBy),
    newState.direction,
  );

  return newState;
}

function filterByTypes(collections: Collection[], filterType: CollectionFilterReducerFilterType) {
  const filters: ConversationMessageContentType[] = [];

  switch (filterType) {
    case 'media':
      filters.push(ConversationMessageContentType.Image, ConversationMessageContentType.Video);
      break;
    case 'text':
      filters.push(ConversationMessageContentType.Text);
      break;
    case 'files':
      filters.push(ConversationMessageContentType.File);
      break;
    case 'audio':
      filters.push(ConversationMessageContentType.Audio);
      break;
    default:
      return collections;
  }

  return collections.filter((c) => filters.includes(c.Type));
}

function groupCollections(collections: Collection[], direction: 'forward' | 'backward'): Record<string, Collection[]> {
  return groupBy(
    sortBy(collections, (c) => (direction === 'backward' ? -c.TimeStamp : c.TimeStamp)),
    (item) => `${generateSessionKey(item.FromType, item.FromID)}-${new Date(item.TimeStamp).toDateString() ?? ''}`,
  );
}
